type Cue = "place" | "deny" | "cargo" | "produce" | "spawn" | "shot" | "impact" | "destroy" | "victory" | "defeat";

class AmberlineAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted = false;
  private musicTimer = 0;
  private beat = 0;
  private intensity = 0.25;
  private lastCue = new Map<Cue, number>();

  unlock() {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = this.muted ? 0 : 0.22;
      this.master.connect(this.context.destination);
      this.startPulse();
    }
    void this.context.resume();
  }

  setMuted(muted: boolean) {
    this.muted = muted;
    if (this.master && this.context) this.master.gain.setTargetAtTime(muted ? 0 : 0.22, this.context.currentTime, 0.03);
  }

  isMuted() { return this.muted; }

  setIntensity(value: number) { this.intensity = Math.max(0.1, Math.min(1, value)); }

  play(cue: Cue, enemy = false) {
    if (!this.context || !this.master || this.muted) return;
    const nowMs = performance.now();
    const throttle = cue === "shot" || cue === "impact" ? 75 : cue === "cargo" ? 180 : 0;
    if (nowMs - (this.lastCue.get(cue) ?? 0) < throttle) return;
    this.lastCue.set(cue, nowMs);
    const tones: Record<Cue, [number, number, OscillatorType, number]> = {
      place: [180, 420, "triangle", 0.12], deny: [92, 66, "sawtooth", 0.14], cargo: [620, 880, "sine", 0.07],
      produce: [260, 520, "triangle", 0.15], spawn: [120, 240, "square", 0.18], shot: [210, 110, "square", 0.055],
      impact: [85, 48, "sawtooth", 0.08], destroy: [110, 35, "sawtooth", 0.32], victory: [220, 660, "triangle", 0.8],
      defeat: [160, 45, "sawtooth", 0.8],
    };
    const [from, to, type, duration] = tones[cue];
    this.tone(enemy ? from * 0.88 : from, enemy ? to * 0.88 : to, duration, type, cue === "destroy" ? 0.18 : 0.09);
  }

  private tone(from: number, to: number, duration: number, type: OscillatorType, level: number) {
    if (!this.context || !this.master) return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(from, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, to), now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(level, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain); gain.connect(this.master);
    oscillator.start(now); oscillator.stop(now + duration + 0.02);
  }

  private startPulse() {
    if (this.musicTimer) window.clearInterval(this.musicTimer);
    this.musicTimer = window.setInterval(() => {
      if (!this.context || this.context.state !== "running" || this.muted) return;
      const scale = [55, 55, 65.41, 55, 73.42, 65.41, 49, 55];
      const frequency = scale[this.beat % scale.length];
      const accent = this.beat % 4 === 0;
      this.tone(frequency, frequency * (accent ? 1.01 : 0.99), 0.16, "triangle", (accent ? 0.035 : 0.018) * this.intensity);
      if (this.intensity > 0.62 && this.beat % 2 === 1) this.tone(880, 180, 0.025, "square", 0.012);
      this.beat += 1;
    }, 360);
  }
}

export const audio = new AmberlineAudio();
